import classNames from "classnames";
import { FC, useEffect, useState } from "react";
import { DocumentGridStateType, ISaveFocus } from "../../../@types/documents";
import { DocumentStatusType, DocumentType, IdTableVariant, LoadingStatus } from "../../../@types/enumsGlobal";
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import { PluginWrapper, ToolbarWrapper } from "../../../components/plugins";
import useGridFilter, { DefaultDateGridFilter } from "../../../system/hooks/useGridFilter";
import { useDetailsTabsPanel } from "../../../system/hooks/useTabsPanel";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { usePluginContext } from "../../../system/providers/plugin";
import baseStyles from "../../styles/index.module.scss";
import tabsStyles from "../../../pages/styles/homePage.module.scss";
import { TabsPanel } from "../../../components/tabs";
import { useTabsContext } from "../../../system/providers/tabsProvider";
import { ActRevaluationDataProvider } from "../../../Services/DataProviders/ActRevaluationDataProvider";
import { IActRevaluationViewDTO } from "../../../libs/coreapi-dto/documents/ActRevaluation";
import DefaultDocumentsCommandsPanelV3 from "../../../components/commandsPanels/DefaultDocumentsCommandsPanelV3";
import { Spinner } from "../../../components/spiner/Spinner";
import { UserActionLogModal } from "../../Service/UserActionLog/view/UserActionLogModal";

export const ActRevaluationPluginView: FC<IGridProps> = (props) => {
    const appContext = useAppContext();
    const pluginCtx = usePluginContext();
    const tabContext = useTabsContext();
    const actRevaluationDP = new ActRevaluationDataProvider(appContext.coreApiService);
    const [data, setData] = useState<IActRevaluationViewDTO[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [totalSums, setTotalSums] = useState<ISumsInfoViewDTO | undefined>();
    const [viewState, setViewState] = useState<DocumentGridStateType>("view");
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [selectedItems, setSelectedItems] = useState<IGridRow[]>([]);
    const [multipleSelect, setMultipleSelect] = useState<boolean>(false);
    const [gridFilter, dispatchGridFilter] = useGridFilter(props.baseGridFilter ?? DefaultDateGridFilter());
    const [documentStatusType, setDocumentStatusType] = useState<DocumentStatusType | undefined>();
    const [selectedDocumentState, setSelectedDocumentState] = useState<DocumentStatusType | undefined>();
    const [detailsTabsPanel, dispatchDetailsTabsPanel] = useDetailsTabsPanel(props.plugin.mnemocode);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [selectedItemView, setSelectedItemViews] = useState<IActRevaluationViewDTO>();


    // ModalLog
    const [showModalLog, setShowModalLog] = useState<boolean>(false);

    const [saveFocus, setSaveFocus] = useState<ISaveFocus>({
        viewState: 'view',
        selectedItem: selectedItem,
        data: data
    });

    let locker = false;

    const loadData = (filter: IGridFilter) => {
        setIsSubmitting(true);
        actRevaluationDP.getViewParam(filter, (entity, totalCount, sums) => {
            try {
                setData(entity);
                setTotalCount(totalCount);
                setTotalSums(sums);
            } finally {
                setIsSubmitting(false);
                if (locker)
                    setTimeout(() => locker = false, 500);
            }
        });
    };

    useEffect(() => {
        if (viewState === "refresh") {
            loadData(gridFilter);
            setViewState("view");
        }
    }, [viewState]);

    useEffect(() => {
        try {
            if (!locker)
                loadData(gridFilter);
        } catch {
        }
    }, [gridFilter]);

    useEffect(() => {
        try {
            locker = true;
            if (!isSubmitting && props.baseGridFilter)
                loadData(props.baseGridFilter);
            else locker = false
        } catch {
        }
    }, [props.baseGridFilter]);

    useEffect(() => {
        if (!multipleSelect) {
            setSelectedItems([]);
        } else {
            setSelectedItem(undefined);
        }
        if (selectedItem) {
            const documentState = selectedItem.cells.find((c) => c.propertyName === "documentState")?.value as DocumentStatusType;
            setSelectedDocumentState(documentState ?? undefined);
            setSelectedItemViews(data.find((x) => x.idGlobal === selectedItem.idGlobal));
        } else {
            setSelectedItemViews(undefined);
        }
    }, [multipleSelect, selectedItem]);

    useEffect(() => {
        pluginCtx.masterGrid.onUnselectEvent();
    }, [tabContext.currentTab]);

    return (
        <PluginWrapper>
            {(!props.extraOptions || props.extraOptions?.showToolbar) && <ToolbarWrapper>
                <DefaultDocumentsCommandsPanelV3
                    selectedItems={[selectedItems,setSelectedItems]}
                    selectedItem={[selectedItem, setSelectedItem]}
                    multipleSelect={[multipleSelect, setMultipleSelect]}
                    saveFocus={[saveFocus, setSaveFocus]}
                    gridFilter={[gridFilter, dispatchGridFilter]}
                    plugin="act_revaluation_creator_plugin"
                    documentStatusType={[documentStatusType, setDocumentStatusType]}
                    selectedDocumentStatusType={[selectedDocumentState, setSelectedDocumentState]}
                    dataProvider={actRevaluationDP}
                    pluginSettings={props.plugin}
                    setViewState={(vs) => setViewState(vs)}
                    idTable={IdTableVariant.ActRevaluation2}
                    mnemocode={selectedItemView?.mnemocode}
                    items={[
                        {
                            label: "Просмотреть лог пользователя",
                            onClick: () => selectedItem && setShowModalLog(true),
                            disabled: selectedItem ? false : true,
                        },
                    ]}
                    advancedFeatures
                    checkLockingDocuments
                />
            </ToolbarWrapper>}
            <div className={baseStyles.gridWrapper}>
                <div className={baseStyles.masterGrid}>
                    {isSubmitting ? (
                        <Spinner />
                    ) : (
                        <DefaultGrid
                            showQuantity
                            openWithEnterForEdit={{
                            pluginTabContext:"act_revaluation_creator_plugin",
                            mnemocode:selectedItemView?.mnemocode,
                            selectedDocumentState}}
                            documentStatus
                            gridId={props.gridId}
                            data={data}
                            filter={gridFilter}
                            dataProvider={actRevaluationDP}
                            totalCount={totalCount}
                            sums={totalSums}
                            plugin={props.plugin}
                            separator
                            selectedItem={selectedItem}
                            selectedItems={selectedItems}
                            saveFocus={[saveFocus, setSaveFocus]}
                            multipleSelect={multipleSelect}
                            isServerFilter
                            searching
                            getView={(gridFilter,callback) => {
                                actRevaluationDP.getViewParam(gridFilter, (entities) => {
                                    callback(entities)
                                    setSelectedItem(undefined);
                                })
                            }}
                            onMultipleSelect={(rows) => {
                                setSelectedItems(rows);
                                rows && props?.onMultipleSelect?.(rows);
                            }}
                            onSelect={(row) => {
                                setSelectedItem(row);
                                row ? pluginCtx.masterGrid.onSelectEvent(row, DocumentType.revaluation) : pluginCtx.masterGrid.onUnselectEvent();
                                row && props?.onSelect?.(row);
                            }}
                            onSort={(i) => {
                                dispatchGridFilter({ type: "sort", payload: i.propertyName });
                            }}
                            onFilterDelete={(i) => {
                                i.propertyName === "documentState" && setDocumentStatusType(undefined);
                                dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName });
                            }}
                            onPageNumberChange={(n) => {
                                dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } });
                            }}
                            onNumberPerPageChange={(n) => {
                                dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } });
                            }}
                        />
                    )}
                </div>
                {(!props.extraOptions || props.extraOptions?.showItems) &&
                    <div className={baseStyles.detailsTabView}>
                        {detailsTabsPanel.tabs.length > 0 && selectedItem && !multipleSelect &&(
                            <>
                                <div className={tabsStyles.tabsPanelWrapper}>
                                    <div id="detailsTabsPanelWrapper" style={{ position: "relative", overflow: "hidden", width: "100%" }}>
                                        <TabsPanel
                                            id="detailsTabsPanel"
                                            activeId={detailsTabsPanel.currentTab?.id}
                                            tabs={detailsTabsPanel.tabs}
                                            onActive={(id) => {
                                                dispatchDetailsTabsPanel({ type: "activate", payload: id });
                                            }}
                                        ></TabsPanel>
                                    </div>
                                </div>
                                <div className={tabsStyles.contentWrapper}>
                                    {detailsTabsPanel.tabs.map((item) => {
                                        return (
                                            <div key={item.id} className={classNames(item.id === detailsTabsPanel.currentTab?.id ? tabsStyles.contentVisible : tabsStyles.contentHidden)}>
                                                {item.view.content}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                }
            </div>
            {showModalLog && <UserActionLogModal idDocumentGlobal={selectedItem?.idGlobal} onClick={() => setShowModalLog(false)} />}
        </PluginWrapper>
    );
};
